import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import Icon from '../../../hive/ui/Icon';
import { useFairTracker } from '../data/FairTrackerStore';
import {
  Block,
  Check,
  Chips,
  Field,
  Input,
  Modal,
  NumInput,
  Pill,
  Segment,
  Select,
  Stars,
  Textarea,
  useToast,
} from '../ui';
import { PhotoGrid, VideoGrid, VoiceList } from '../media/MediaGrid';
import useMedia from '../media/useMedia';
import useRecorder from '../media/useRecorder';
import { newContact, newProduct, newSupplier } from '../data/entities';
import {
  CERT_STATES,
  CERT_TYPES,
  CURRENCIES,
  DECISIONS,
  HANDOVER,
  LEN_UNITS,
  NEXT_STEPS,
  PAY_METHODS,
  PRICE_UNITS,
  SAMPLE_STATES,
  WT_UNITS,
} from '../data/constants';
import {
  autoFollowUp,
  findDuplicate,
  linkPlanItem,
  marginCalc,
  marginTone,
  money,
  num,
  quickEntryDefaults,
  syncShortlist,
  toUSD,
} from '../rules';
import { p } from '../routes';
import { tr } from '../i18n';

/** Rule Q1 stated for a human: shown on both candidate fields and in the toast. */
const CORE_REQUIRED = 'Add a product name or a company — either one is enough.';

/**
 * Quick Entry (spec § 5.4) — the most important screen in the app.
 *
 * The field order is deliberate and must not be "improved":
 *   Photos → Product → Supplier → Price & MOQ → Rate it → Save
 * Everything else is collapsed under "More details (optional)". A buyer has
 * about two minutes at a booth, and that order is what fits in it: shoot first
 * (the supplier is standing right there), name it, then the numbers.
 *
 * Nothing on this path awaits the network. Save writes IndexedDB and queues
 * the outbox; the toast fires on the local write.
 *
 * Query params (spec § 4):
 *   ?supplier=id  add a product to an existing supplier (hides Supplier/Contact)
 *   ?plan=id      log against a plan item, and return to the Plan afterwards
 */
export default function EntryScreen() {
  const history = useHistory();
  const location = useLocation();
  const toast = useToast();
  const {
    fairs,
    fair,
    suppliers,
    visibleSuppliers,
    currentFair,
    settings,
    saveSupplier,
    saveFair,
  } = useFairTracker();

  const params = new URLSearchParams(location.search);
  const existingId = params.get('supplier') || '';
  const planId = params.get('plan') || '';

  const existing = useMemo(
    () => (existingId ? suppliers.find((s) => s.id === existingId) : null),
    [existingId, suppliers],
  );

  const defaultFairId = currentFair && currentFair !== 'all' ? currentFair : (fairs[0] || {}).id || '';

  /* ── Draft state ──────────────────────────────────────────────────────── */
  const blankSupplier = useCallback(
    () =>
      newSupplier({
        fairId: defaultFairId,
      }),
    [defaultFairId],
  );

  const blankProduct = useCallback(
    () =>
      newProduct({
        currency: (settings && settings.currency) || 'USD',
        lenUnit: (settings && settings.len) || 'cm',
        wtUnit: (settings && settings.wt) || 'kg',
        planId,
      }),
    [settings, planId],
  );

  const [sup, setSup] = useState(blankSupplier);
  const [prod, setProd] = useState(blankProduct);
  const [contact, setContact] = useState(() => newContact());
  const [photos, setPhotos] = useState([]);
  const [videos, setVideos] = useState([]);
  const [voice, setVoice] = useState([]);
  const [dupe, setDupe] = useState(null);
  const [noteOpen, setNoteOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  // Only set once Save has been pressed — nothing on this screen goes red
  // while a buyer is still filling it in.
  const [submitted, setSubmitted] = useState(false);

  const media = useMedia({ supplierId: sup.id, productId: prod.id });
  const recorder = useRecorder({
    onDone: async ({ blob, duration }) => {
      setVoice(await media.addVoice({ existing: voice, blob, duration, forProductId: prod.id }));
      toast('Voice note saved');
    },
  });

  const topRef = useRef(null);

  /* When ?supplier= is set we are adding a product to a known supplier, so the
     Supplier and Contact blocks are hidden and its fair is inherited. */
  useEffect(() => {
    if (existing) setSup((s) => ({ ...s, fairId: existing.fairId }));
  }, [existing]);

  /* Arriving from "Found it — log supplier" on a plan item: seed the product
     name from that item.

     The spec only promises this when the buyer picks from the "From my fair
     plan" select, but they have already named the thing once by creating the
     plan entry and again by tapping its button — making them type "Desk lamp"
     a third time, at a booth, on the path that is meant to take two minutes,
     is the kind of friction this screen exists to remove. Only fills an empty
     field, so it never overwrites typing. */
  useEffect(() => {
    if (!planId) return;
    // Search every fair's plan, not just the active one — the id is unique and
    // this stays correct if the buyer switched fairs between tapping and here.
    const item = fairs
      .flatMap((f) => f.plan || [])
      .find((x) => x.id === planId);
    if (!item || !item.name) return;
    setProd((x) => (x.name ? x : { ...x, name: item.name }));
  }, [planId, fairs]);

  const setS = (k) => (v) => setSup((x) => ({ ...x, [k]: v }));
  const setP = (k) => (v) => setProd((x) => ({ ...x, [k]: v }));
  const setC = (k) => (v) => setContact((x) => ({ ...x, [k]: v }));

  /* ── D1 — duplicate check on blur ─────────────────────────────────────── */
  const checkDuplicate = () => {
    if (existing) return;
    const hit = findDuplicate({
      suppliers,
      fairId: sup.fairId,
      company: sup.company,
      excludeId: sup.id,
    });
    setDupe(hit);
  };

  /* ── FX hint and M1 margin ────────────────────────────────────────────── */
  const activeFair = existing
    ? fairs.find((f) => f.id === existing.fairId) || fair
    : fairs.find((f) => f.id === sup.fairId) || fair;

  const fxHint = (() => {
    if (!prod.price || prod.currency === 'USD') return null;
    const usd = toUSD(num(prod.price), prod.currency, activeFair);
    if (usd == null) return `Set the ${prod.currency}→USD rate in Edit fair`;
    return `≈ ${money(usd)} at this fair's rate`;
  })();

  const margin = marginCalc(prod, activeFair, settings);

  /* ── Rule Q1, surfaced in the form ────────────────────────────────────────
     Q1 is an EITHER/OR, not two required fields: a product name alone is
     enough, and so is a company alone (rules/index.js fills the other in).
     That is why both fields carry the asterisk and both go red together — and
     why the message spells the either/or out, which an asterisk on its own
     cannot. Derived, not stored, so the red clears the moment either field has
     something in it rather than waiting for the next Save.
     ───────────────────────────────────────────────────────────────────────── */
  const coreMissing =
    !(prod.name || '').trim() && !((existing ? existing.company : sup.company) || '').trim();
  const coreError = submitted && coreMissing ? CORE_REQUIRED : '';

  const productRef = useRef(null);

  /* ── Save (rule Q1) ───────────────────────────────────────────────────── */
  /**
   * ONE destination, always My Suppliers.
   *
   * This used to be three buttons with three outcomes — "Save & view" went to
   * the new supplier, "Save & next" cleared the form in place, and a plan entry
   * went back to the Plan. Three ways to leave one screen is three things to
   * read at a booth, and the one the buyer actually wants next is the list of
   * what they have captured. So: save, say so, go to the list.
   */
  const save = async () => {
    if (saving) return;
    setSubmitted(true);

    const productIndex = existing ? (existing.products || []).length : 0;
    const defaults = quickEntryDefaults({
      productName: prod.name,
      company: existing ? existing.company : sup.company,
      productIndex,
    });
    if (!defaults.ok) {
      // The same sentence the two fields show, not the rule's own `reason` —
      // one problem should not arrive worded two different ways at once.
      toast(tr(CORE_REQUIRED), 'error');
      // The Save button is in the sticky bar at the bottom, so the fields that
      // just went red can easily be off-screen above it. Showing an error the
      // buyer has to go hunting for is barely better than not showing one.
      if (productRef.current) productRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    setSaving(true);
    try {
      // The product carries its media and the resolved name.
      const product = { ...prod, name: defaults.productName, voiceNotes: voice };

      // Media references are stamped with the product so the photo grid and
      // rule P1's main-photo fallback can find them.
      const stamp = (list) => list.map((m) => ({ ...m, productId: product.id }));

      let target;
      if (existing) {
        target = {
          ...existing,
          products: [...(existing.products || []), product],
          photos: [...(existing.photos || []), ...stamp(photos)],
          videos: [...(existing.videos || []), ...stamp(videos)],
        };
      } else {
        target = {
          ...sup,
          company: defaults.company,
          products: [product],
          photos: stamp(photos),
          videos: stamp(videos),
        };

        // A contact is created when ANY contact field is filled, or a business
        // card was photographed (spec § 5.4). The first contact is primary, and
        // business-card photos are copied into it (rule Q1).
        const hasContactField = ['phone', 'wechat', 'whatsapp', 'email', 'website', 'instagram', 'linkedin', 'other']
          .some((k) => (contact[k] || '').trim());
        const cardPhotos = photos.filter((ph) => ph.tag === 'Business Card');

        if (hasContactField || cardPhotos.length) {
          target.contacts = [
            { ...contact, primary: true, photos: cardPhotos.map((ph) => ({ ...ph, tag: 'Business Card' })) },
          ];
        }
      }

      // S1 then F1/F2, in the prototype's order.
      target = syncShortlist(target);
      target.followUps = autoFollowUp(target, product);

      await saveSupplier(target);

      // PL1 — link the plan item it was logged against.
      if (planId && activeFair) {
        await saveFair(linkPlanItem(activeFair, planId, target.id, product.id));
      }

      // The toast is raised BEFORE the push on purpose. ToastHost sits above
      // the router in FairTracker.jsx, so it outlives this screen unmounting
      // and the confirmation is still on screen once My Suppliers has painted.
      toast(existing ? tr('Product added') : tr('Supplier saved'));
      history.push(p('/suppliers'));
    } catch (e) {
      toast(tr('Could not save on this device — nothing was lost, try again'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const planItems = ((activeFair && activeFair.plan) || []).filter((it) => !it.found || (it.matches || []).length);

  return (
    <div className="ft-page ft-entry" ref={topRef}>
      <div className="ft-title">
        <h1 className="ft-h1">{existing ? tr(`Add a product to ${existing.company}`) : tr('Quick Entry')}</h1>
        <p className="ft-sub" style={{ margin: 0 }}>
          {tr("Photo → product → supplier → price → rate → Save.")}
        </p>
      </div>

      {!existing && visibleSuppliers.length > 0 && (
        <Field label={tr("Adding to an existing supplier?")}>
          <Select
            value=""
            placeholder={tr("No — this is a new supplier")}
            options={visibleSuppliers.map((s) => ({ value: s.id, label: s.company }))}
            onChange={(id) => id && history.push(`${p('/entry')}?supplier=${id}`)}
          />
        </Field>
      )}

      {/* Two columns from 1100px up. The photo→product→supplier sequence stays
          together in the first column: that order IS the two-minute capture
          path and must not be split across columns. */}
      <div className="ft-entry-cols">
      {/* ── 1. PHOTOS ──────────────────────────────────────────────────── */}
      <section className="ft-card">
        <h2 className="ft-h2">{tr("Photos")}</h2>
        <div className="ft-btnrow">
          <button
            type="button"
            className="ft-btn ft-btn-primary"
            onClick={async () =>
              setPhotos(await media.addPhotos({ existing: photos, fromCamera: true, forProductId: prod.id }))
            }
          >
            <Icon name="Camera" size={17} /> {tr("Take photo")}
          </button>
          <button
            type="button"
            className="ft-btn"
            onClick={async () => setPhotos(await media.addPhotos({ existing: photos, forProductId: prod.id }))}
          >
            <Icon name="Upload" size={17} /> {tr("Upload")}
          </button>
          <button
            type="button"
            className="ft-btn"
            onClick={async () => setVideos(await media.addVideo({ existing: videos, forProductId: prod.id }))}
          >
            <Icon name="Video" size={17} /> {tr('Video')}
          </button>
          <button
            type="button"
            className={`ft-btn${recorder.recording ? ' ft-btn-danger' : ''}`}
            onClick={recorder.toggle}
          >
            <Icon name="Mic" size={17} />
            {recorder.recording ? `Stop ${recorder.seconds}s` : tr('Voice note')}
          </button>
          <button type="button" className="ft-btn" onClick={() => setNoteOpen(true)}>
            <Icon name="FileText" size={17} /> {tr('Add note')}
          </button>
        </div>

        <p className="ft-hint" style={{ marginTop: 10 }}>{tr("Also shoot:")}</p>
        <div className="ft-chips">
          {['Booth', 'Business Card', 'Document'].map((tag) => (
            <button
              key={tag}
              type="button"
              className="ft-chip"
              onClick={async () =>
                setPhotos(
                  await media.addPhotos({ existing: photos, tag, fromCamera: true, forProductId: prod.id }),
                )
              }
            >
              {tag === 'Document' ? tr('Brochure / spec') : tr(tag)}
            </button>
          ))}
        </div>

        <PhotoGrid
          photos={photos}
          onRetag={(ph, tag, pid) => setPhotos(media.retag(photos, ph, tag, pid))}
          onRemove={async (ph) => setPhotos(await media.remove(photos, ph))}
        />
        <VideoGrid videos={videos} onRemove={async (v) => setVideos(await media.remove(videos, v))} />
        <VoiceList notes={voice} onRemove={async (v) => setVoice(await media.remove(voice, v))} />
      </section>

      {/* ── 2. PRODUCT ─────────────────────────────────────────────────── */}
      <section className="ft-card" ref={productRef}>
        <h2 className="ft-h2">{tr("Product")}</h2>
        {planItems.length > 0 && (
          <Field label={tr("From my fair plan")}>
            <Select
              value={prod.planId || ''}
              placeholder={tr("Not from my plan")}
              options={planItems.map((it) => ({ value: it.id, label: it.name }))}
              onChange={(id) => {
                const item = planItems.find((x) => x.id === id);
                setProd((x) => ({ ...x, planId: id, name: x.name || (item ? item.name : '') }));
              }}
            />
          </Field>
        )}
        <Field label={tr("Product / item")} required error={coreError}>
          <Input value={prod.name} onChange={setP('name')} placeholder="LED pendant lamp" />
        </Field>
      </section>

      {/* ── 3. SUPPLIER ────────────────────────────────────────────────── */}
      {!existing && (
        <section className="ft-card">
          <h2 className="ft-h2">{tr("Supplier")}</h2>
          {fairs.length > 1 && (
            <Field label={tr("Fair")}>
              <Select
                value={sup.fairId}
                options={fairs.map((f) => ({ value: f.id, label: f.name }))}
                onChange={setS('fairId')}
              />
            </Field>
          )}
          <Field label={tr("Supplier / company")} required error={coreError}>
            <Input
              value={sup.company}
              onChange={setS('company')}
              onBlur={checkDuplicate}
              placeholder="Acme Lighting Co., Ltd"
            />
          </Field>

          {dupe && (
            <div className="ft-dupe">
              {tr("Looks like")} <b>{dupe.company}</b> {tr("is already saved.")}{' '}
              <Link to={`${p('/entry')}?supplier=${dupe.id}`}>{tr("Add this product to that supplier instead")}</Link>
            </div>
          )}
        </section>
      )}

      {/* ── 4. PRICE & MOQ ─────────────────────────────────────────────── */}
      <section className="ft-card">
        <h2 className="ft-h2">{tr("Price & MOQ")}</h2>
        <div className="ft-grid-2">
          <Field label={tr("Quoted price")} hint={fxHint}>
            <NumInput value={prod.price} onChange={setP('price')} placeholder="12.50" />
          </Field>
          <Field label={tr("Currency")}>
            <Select value={prod.currency} options={CURRENCIES} onChange={setP('currency')} />
          </Field>
          <Field label={tr("MOQ")}>
            <NumInput value={prod.moq} onChange={setP('moq')} placeholder="500" />
          </Field>
          <Field label={tr("Price is per")}>
            <Select value={prod.priceUnit} options={PRICE_UNITS} onChange={setP('priceUnit')} />
          </Field>
        </div>

        <Field label={tr("I would sell it at (USD)")} hint={tr("Gives you the margin before you leave the booth.")}>
          <NumInput value={prod.sellPrice} onChange={setP('sellPrice')} placeholder="29.99" />
        </Field>

        {margin && (
          <div className="ft-landed">
            <div>
              <span>Est. landed (×{margin.factor})</span>
              <b>{money(margin.landedEst)}</b>
            </div>
            <div>
              <span>Margin at {money(num(prod.sellPrice))}</span>
              <b className={`ft-tone-${marginTone(margin.margin)}`}>{margin.margin.toFixed(0)}%</b>
            </div>
            <div>
              <span>{tr("Profit / unit")}</span>
              <b className={`ft-tone-${marginTone(margin.margin)}`}>{money(margin.profit)}</b>
            </div>
          </div>
        )}
      </section>

      {/* ── 5. RATE IT ─────────────────────────────────────────────────── */}
      <section className="ft-card">
        <h2 className="ft-h2">{tr("Rate it")}</h2>
        <Stars value={prod.rating} onChange={setP('rating')} />
        <Field label={tr("Decision")}>
          <Segment value={prod.decision} onChange={setP('decision')} options={DECISIONS} tone="decision" />
        </Field>
        <Check
          checked={prod.revisit}
          onChange={setP('revisit')}
          label={tr("Revisit this booth")}
          tone="amber"
        />
        {prod.revisit && (
          <div className="ft-grid-2" style={{ marginTop: 8 }}>
            <Field label={tr("Reason to return")}>
              <Input value={prod.revisitReason} onChange={setP('revisitReason')} placeholder={tr("See the samples")} />
            </Field>
            <Field label={tr("Appointment")} hint={tr("Creates a follow-up automatically.")}>
              <Input
                type="datetime-local"
                value={prod.revisitAt}
                onChange={setP('revisitAt')}
              />
            </Field>
          </div>
        )}
      </section>

      {/* ── MORE DETAILS (collapsed) ───────────────────────────────────── */}
      <div className="ft-entry-more">
      <Block title={tr("More details (optional)")}>
        {!existing && (
          <Block title={tr("Contact information")}>
            <div className="ft-grid-2">
              <Field label={tr("Phone")}>
                <Input value={contact.phone} onChange={setC('phone')} type="tel" />
              </Field>
              <Field label="WeChat">
                <Input value={contact.wechat} onChange={setC('wechat')} />
              </Field>
              <Field label="WhatsApp">
                <Input value={contact.whatsapp} onChange={setC('whatsapp')} type="tel" />
              </Field>
              <Field label={tr("Email")}>
                <Input value={contact.email} onChange={setC('email')} type="email" />
              </Field>
              <Field label={tr("Website")}>
                <Input value={contact.website} onChange={setC('website')} />
              </Field>
              <Field label={tr("Instagram")}>
                <Input value={contact.instagram} onChange={setC('instagram')} />
              </Field>
              <Field label="LinkedIn">
                <Input value={contact.linkedin} onChange={setC('linkedin')} />
              </Field>
              <Field label={tr("Other")}>
                <Input value={contact.other} onChange={setC('other')} />
              </Field>
            </div>
          </Block>
        )}

        <Block title={tr("Notes & next step")}>
          <Field label={tr("Short description")}>
            <Input value={prod.description} onChange={setP('description')} />
          </Field>
          <Field label={tr("Notes")}>
            <Textarea value={prod.notes} onChange={setP('notes')} rows={4} />
          </Field>
          <Field label={tr("Next step")} hint={tr("Creates a follow-up automatically.")}>
            <Segment value={prod.nextStep} onChange={setP('nextStep')} options={NEXT_STEPS} />
          </Field>
        </Block>

        <Block title={tr("More pricing")}>
          <div className="ft-grid-2">
            <Field label={tr("Quantity for that price")}>
              <NumInput value={prod.qty} onChange={setP('qty')} />
            </Field>
            <Field label={tr("Best price negotiated")}>
              <NumInput value={prod.bestPrice} onChange={setP('bestPrice')} />
            </Field>
            <Field label={tr("Quote valid until")}>
              <Input type="date" value={prod.validUntil} onChange={setP('validUntil')} />
            </Field>
          </div>
          <Tiers tiers={prod.tiers} onChange={setP('tiers')} />
        </Block>

        <Block title={tr("Terms")}>
          <div className="ft-grid-2">
            <Field label={tr("FOB port")}>
              <Input value={prod.fobPort} onChange={setP('fobPort')} placeholder="Shenzhen" />
            </Field>
            <Field label={tr("Payment terms")}>
              <Input value={prod.paymentTerms} onChange={setP('paymentTerms')} placeholder="30% deposit" />
            </Field>
            <Field label={tr("Production time")}>
              <Input value={prod.productionTime} onChange={setP('productionTime')} placeholder="35 days" />
            </Field>
          </div>
          <Field label={tr("Sample")}>
            <Segment value={prod.sample} onChange={setP('sample')} options={SAMPLE_STATES} />
          </Field>
          {prod.sample !== 'No' && (
            <div className="ft-grid-2">
              <Field label={tr("Sample price")}>
                <NumInput value={prod.samplePrice} onChange={setP('samplePrice')} />
              </Field>
              <Field label={tr("Sample shipping")}>
                <NumInput value={prod.sampleShipping} onChange={setP('sampleShipping')} />
              </Field>
              <Field label={tr("Sample notes")} className="ft-span2">
                <Input value={prod.sampleNotes} onChange={setP('sampleNotes')} />
              </Field>
            </div>
          )}
        </Block>

        <Block title={tr("Shipping information")}>
          <div className="ft-grid-2">
            <Field label={tr("Carton L")}>
              <NumInput value={prod.cartonL} onChange={setP('cartonL')} />
            </Field>
            <Field label={tr("Carton W")}>
              <NumInput value={prod.cartonW} onChange={setP('cartonW')} />
            </Field>
            <Field label={tr("Carton H")}>
              <NumInput value={prod.cartonH} onChange={setP('cartonH')} />
            </Field>
            <Field label={tr("Length unit")}>
              <Select value={prod.lenUnit} options={LEN_UNITS} onChange={setP('lenUnit')} />
            </Field>
            <Field label={tr("Carton weight")}>
              <NumInput value={prod.cartonWeight} onChange={setP('cartonWeight')} />
            </Field>
            <Field label={tr("Weight unit")}>
              <Select value={prod.wtUnit} options={WT_UNITS} onChange={setP('wtUnit')} />
            </Field>
            <Field label={tr("Units per carton")}>
              <NumInput value={prod.unitsPerCarton} onChange={setP('unitsPerCarton')} />
            </Field>
          </div>
        </Block>

        <Block title={tr("USA certification")}>
          <Field label={tr("Has US certification?")}>
            <Segment value={prod.cert} onChange={setP('cert')} options={CERT_STATES} />
          </Field>
          <Chips value={prod.certTypes} onChange={setP('certTypes')} options={CERT_TYPES} />
          {prod.certTypes.includes('Other') && (
            <Field label={tr("Which other?")}>
              <Input value={prod.certOther} onChange={setP('certOther')} />
            </Field>
          )}
        </Block>

        <Block title={tr("Money paid at this booth")}>
          <div className="ft-grid-2">
            <Field label={tr("Sample paid")}>
              <NumInput value={prod.samplePaid} onChange={setP('samplePaid')} />
            </Field>
            <Field label={tr("Paid with")}>
              <Select value={prod.samplePayMethod} placeholder="—" options={PAY_METHODS} onChange={setP('samplePayMethod')} />
            </Field>
            <Field label={tr("Deposit paid")}>
              <NumInput value={prod.depositPaid} onChange={setP('depositPaid')} />
            </Field>
            <Field label={tr("Paid with")}>
              <Select value={prod.depositMethod} placeholder="—" options={PAY_METHODS} onChange={setP('depositMethod')} />
            </Field>
          </div>
          <Field label={tr("Sample handover")}>
            <Segment value={prod.sampleHandover} onChange={setP('sampleHandover')} options={HANDOVER} />
          </Field>
          <div className="ft-grid-2">
            <Field label={tr("Pick-up / ship date")}>
              <Input type="date" value={prod.samplePickup} onChange={setP('samplePickup')} />
            </Field>
            <Field label={tr("Notes")}>
              <Input value={prod.depositNotes} onChange={setP('depositNotes')} />
            </Field>
          </div>
          <button
            type="button"
            className="ft-chip"
            onClick={async () =>
              setPhotos(
                await media.addPhotos({
                  existing: photos,
                  tag: 'Receipt',
                  fromCamera: true,
                  forProductId: prod.id,
                }),
              )
            }
          >
            <Icon name="Camera" size={14} /> {tr("Photo of receipt")}
          </button>
        </Block>
      </Block>
      </div>
      </div>

      {/* ── STICKY SAVE BAR ────────────────────────────────────────────── */}
      <div className="ft-stickybar">
        <Link className="ft-btn ft-btn-ink ft-btn-sm" to={p('/card')}>
          Show my card 名片
        </Link>
        <div className="ft-stickybar-main">
          <button type="button" className="ft-btn ft-btn-primary" disabled={saving} onClick={save}>
            {saving ? tr('Saving…') : tr('Save')}
          </button>
        </div>
      </div>

      {/* Rule N1 — the microphone was unavailable, so a typed/dictated note.
          NEVER a file picker: on iOS that opens the camera. */}
      {recorder.unavailable && (
        <NoteModal
          title={tr("Note (type or dictate)")}
          hint={tr("Your microphone is not available here. Use your keyboard's dictation key — the text lands in the product notes.")}
          onClose={recorder.dismissUnavailable}
          onSave={(text) => {
            setProd((x) => ({ ...x, notes: [x.notes, text].filter(Boolean).join('\n') }));
            recorder.dismissUnavailable();
            toast('Note added');
          }}
        />
      )}

      {noteOpen && (
        <NoteModal
          title={tr("Add note")}
          hint={tr("Type, or use your keyboard's dictation key.")}
          onClose={() => setNoteOpen(false)}
          onSave={(text) => {
            setProd((x) => ({ ...x, notes: [x.notes, text].filter(Boolean).join('\n') }));
            setNoteOpen(false);
            toast('Note added');
          }}
        />
      )}
    </div>
  );
}

/** Price tiers — qty/price pairs with add and remove. */
function Tiers({ tiers = [], onChange }) {
  return (
    <div className="ft-tiers">
      <p className="ft-hint">{tr("Price tiers")}</p>
      {tiers.map((t, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <div className="ft-tier" key={i}>
          <NumInput
            value={t.qty}
            onChange={(v) => onChange(tiers.map((x, j) => (i === j ? { ...x, qty: v } : x)))}
            placeholder={tr("Qty")}
          />
          <NumInput
            value={t.price}
            onChange={(v) => onChange(tiers.map((x, j) => (i === j ? { ...x, price: v } : x)))}
            placeholder={tr("Price")}
          />
          <button
            type="button"
            className="ft-icon-btn"
            aria-label="Remove tier"
            onClick={() => onChange(tiers.filter((_, j) => j !== i))}
          >
            <Icon name="X" size={15} />
          </button>
        </div>
      ))}
      <button type="button" className="ft-chip" onClick={() => onChange([...tiers, { qty: '', price: '' }])}>
        <Icon name="Plus" size={14} /> {tr("Add tier")}
      </button>
    </div>
  );
}

/**
 * The note modal puts its actions at the TOP (spec § 5.6): the on-screen
 * keyboard covers the bottom half of the screen while typing, so Save has to
 * stay reachable.
 */
export function NoteModal({ title, hint, onClose, onSave }) {
  const [text, setText] = useState('');
  return (
    <Modal
      title={title}
      onClose={onClose}
      actionsTop
      actions={
        <>
          <button type="button" className="ft-btn ft-btn-cancel" onClick={onClose}>
            {tr("Cancel")}
          </button>
          <button
            type="button"
            className="ft-btn ft-btn-green"
            disabled={!text.trim()}
            onClick={() => onSave(text.trim())}
          >
            {tr("Save note")}
          </button>
        </>
      }
    >
      {hint && <p className="ft-hint">{hint}</p>}
      <Textarea value={text} onChange={setText} rows={6} autoFocus placeholder={tr("What did they say?")} />
    </Modal>
  );
}

export { Pill };
